import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MobileHeader, HeaderAction, ActionSheet, Chip } from '../components/MobileHeader';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isToday, subDays, addDays, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Check, Plus, Trash2, Edit2, X, ChevronLeft, ChevronRight, BarChart2, CheckSquare, Clock, Flame, RefreshCw, AlertCircle, ChevronDown, FileText, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { cn } from '../lib/utils';
import EntryList from '../components/memo/EntryList';

const TIME_LABELS = { morning: 'Matin', afternoon: 'Après-midi', evening: 'Soir', anytime: 'Tout moment' };
const TIME_ORDER = ['morning', 'anytime', 'afternoon', 'evening'];
const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const COLORS = ['#26A69A','#0288D1','#FF7043','#7B1FA2','#E91E63','#F57C00','#9C27B0','#607D8B','#00ACC1','#43A047'];
const ICONS  = ['⭐','💃','🎵','🧘','💪','💧','📊','📱','🌙','📖','🍎','🏃','🎯','✍️','🧠','🌿'];
const FREQ_LABELS = { daily: 'Quotidien', weekly: 'Hebdomadaire', specific_days: 'Jours spécifiques' };

const fmtDate = d => format(d, 'yyyy-MM-dd');

function isHabitDueOn(habit, d) {
  const dow = (d.getDay() + 6) % 7; // 0=Mon … 6=Sun
  if (habit.frequency === 'daily') return true;
  if (habit.frequency === 'weekly') return true;
  if (habit.frequency === 'specific_days') return (habit.specific_days || []).includes(dow);
  return true;
}

function PctBar({ pct, color, label, sub }) {
  return (
    <div className="flex-1">
      <div className="flex justify-between text-xs mb-1">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className="font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function HabitForm({ habit, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: habit?.name || '',
    description: habit?.description || '',
    color: habit?.color || COLORS[0],
    icon: habit?.icon || '⭐',
    frequency: habit?.frequency || 'daily',
    specific_days: habit?.specific_days || [],
    time_of_day: habit?.time_of_day || 'anytime',
    category: habit?.category || '',
  });

  const toggleDay = d => setForm(f => ({
    ...f, specific_days: f.specific_days.includes(d) ? f.specific_days.filter(x => x !== d) : [...f.specific_days, d]
  }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="bg-white w-full max-w-lg rounded-t-3xl shadow-2xl flex flex-col" style={{ maxHeight: '88dvh' }}>
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100 shrink-0">
          <div className="font-bold text-slate-900">{habit ? 'Modifier' : 'Nouvelle habitude'}</div>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {/* Icon + Name */}
          <div className="flex gap-3 items-start">
            <div className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl border-2 border-slate-200"
              style={{ backgroundColor: form.color + '20', borderColor: form.color }}>
              {form.icon}
            </div>
            <div className="flex-1">
              <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="Nom de l'habitude" className="input-base w-full mb-2" data-testid="habit-name-input" />
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Description (optionnel)" className="input-base w-full text-sm" />
            </div>
          </div>
          {/* Icon picker */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Icône</div>
            <div className="flex flex-wrap gap-2">
              {ICONS.map(ic => (
                <button key={ic} onClick={() => setForm(f => ({...f, icon: ic}))}
                  className={cn('w-10 h-10 rounded-xl text-xl flex items-center justify-center transition-all border-2',
                    form.icon === ic ? 'border-[#0288D1] bg-[#0288D1]/10 scale-110' : 'border-transparent bg-slate-50 hover:bg-slate-100')}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          {/* Color picker */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Couleur</div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({...f, color: c}))}
                  className={cn('w-8 h-8 rounded-full border-4 transition-all', form.color === c ? 'border-slate-700 scale-110' : 'border-transparent hover:scale-105')}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          {/* Frequency */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Fréquence</div>
            <div className="flex gap-2">
              {Object.entries(FREQ_LABELS).map(([val, label]) => (
                <button key={val} onClick={() => setForm(f => ({...f, frequency: val}))}
                  className={cn('flex-1 py-2 rounded-xl text-xs font-bold border transition-colors',
                    form.frequency === val ? 'bg-[#0288D1] text-white border-[#0288D1]' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300')}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Specific days */}
          {form.frequency === 'specific_days' && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Jours</div>
              <div className="flex gap-1.5">
                {DAY_NAMES.map((d, i) => (
                  <button key={i} onClick={() => toggleDay(i)}
                    className={cn('flex-1 py-2 rounded-xl text-xs font-bold border transition-colors',
                      form.specific_days.includes(i) ? 'bg-[#26A69A] text-white border-[#26A69A]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300')}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Time of day */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Moment de la journée</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TIME_LABELS).map(([val, label]) => (
                <button key={val} onClick={() => setForm(f => ({...f, time_of_day: val}))}
                  className={cn('py-2.5 rounded-xl text-sm font-bold border transition-colors',
                    form.time_of_day === val ? 'bg-[#26A69A] text-white border-[#26A69A]' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300')}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Category */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Liste / Catégorie (optionnel)</div>
            <input value={form.category || ''} onChange={e => setForm(f => ({...f, category: e.target.value || null}))}
              placeholder="ex: Finance, Healthy, Projet…" className="input-base w-full" />
          </div>
        </div>
        <div className="shrink-0 px-5 pb-6 pt-3 border-t border-slate-100 flex gap-2">
          <button onClick={onCancel} className="flex-1 h-12 rounded-2xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-colors">
            Annuler
          </button>
          <button onClick={() => onSave(form)} disabled={!form.name.trim()}
            className="flex-1 h-12 rounded-2xl bg-[#26A69A] text-white font-bold hover:bg-[#219e92] transition-colors disabled:opacity-50"
            data-testid="save-habit-btn">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function TaskListCard({ list, tasks, onAddTask, onToggleTask, onDeleteTask, onDeleteList, onUpdateNotes }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [expandedId, setExpandedId] = useState(null);   // tâche dont la note est en cours d'édition
  const [editingNotes, setEditingNotes] = useState('');
  const done = tasks.filter(t => t.completed).length;
  const total = tasks.length;

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    await onAddTask(list.id, newTitle.trim());
    setNewTitle(''); setAdding(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="task-list-card">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-50">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-lg shrink-0"
          style={{ backgroundColor: list.color + '20' }}>
          {list.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900 text-sm">{list.name}</div>
          {total > 0 && <div className="text-xs text-slate-400">{done}/{total} terminées</div>}
        </div>
        <button onClick={() => onDeleteList(list.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition-colors">
          <Trash2 size={13} />
        </button>
      </div>
      {total > 0 && (
        <div className="px-4 py-1.5">
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${total ? Math.round(100*done/total) : 0}%`, backgroundColor: list.color }} />
          </div>
        </div>
      )}
      <div className="divide-y divide-slate-50">
        {tasks.map(task => (
          <div key={task.id} data-testid="task-item">
            <div className="flex items-center gap-3 px-4 py-2.5 group">
              <button onClick={() => onToggleTask(task)}
                className={cn('w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all',
                  task.completed ? 'border-[color] bg-[color]' : 'border-slate-300 hover:border-[color]')}
                style={task.completed ? { backgroundColor: list.color, borderColor: list.color } : { '--color': list.color }}>
                {task.completed && <Check size={11} className="text-white" strokeWidth={3} />}
              </button>
              {/* Toucher le titre : voir / modifier la note */}
              <button onClick={() => { setExpandedId(expandedId === task.id ? null : task.id); setEditingNotes(task.description || ''); }}
                className={cn('flex-1 min-w-0 text-left text-sm', task.completed ? 'line-through text-slate-400' : 'text-slate-800')}>
                {task.title}
                {task.description && expandedId !== task.id && (
                  <span className="block text-[11px] text-slate-400 font-normal mt-0.5 truncate no-underline">{task.description}</span>
                )}
              </button>
              <button onClick={() => onDeleteTask(task.id)}
                className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-red-400 transition-all">
                <X size={12} />
              </button>
            </div>
            {expandedId === task.id && (
              <div className="px-4 pb-3 bg-amber-50/50 border-t border-amber-100/50">
                <textarea autoFocus value={editingNotes} onChange={e => setEditingNotes(e.target.value)}
                  placeholder="Ajouter une note…" rows={3} data-testid="task-notes-input"
                  className="w-full text-sm text-slate-700 bg-transparent resize-none outline-none border-b border-slate-200 pb-1 pt-2 min-h-[50px]" />
                <div className="flex justify-end gap-3 mt-1.5">
                  <button onClick={() => setExpandedId(null)} className="text-xs text-slate-400">Annuler</button>
                  <button onClick={async () => { await onUpdateNotes(task, editingNotes); setExpandedId(null); }}
                    className="text-xs font-bold text-[#26A69A]">Enregistrer</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {adding ? (
        <div className="px-4 py-2.5 border-t border-slate-50 flex items-center gap-2">
          <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }}}
            placeholder="Nouvelle tâche…" className="flex-1 text-sm bg-transparent outline-none" data-testid="new-task-input" />
          <button onClick={handleAdd} className="text-xs font-bold text-[#26A69A]">OK</button>
          <button onClick={() => { setAdding(false); setNewTitle(''); }} className="text-xs text-slate-400">✕</button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-[#26A69A] hover:bg-[#26A69A]/5 transition-colors"
          data-testid="add-task-btn">
          <Plus size={13} /> Ajouter une tâche
        </button>
      )}
    </div>
  );
}

function GoogleTasksPanel({ lists, selectedListId, tasks, loading, onSelectList, onToggle, onDelete, onCreate, onUpdateNotes }) {
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [showDue, setShowDue] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null); // task.id en cours d'édition notes
  const [editingNotes, setEditingNotes] = useState('');

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await onCreate(newTitle, showDue ? newDue : null, showNotes ? newNotes : null);
    setNewTitle(''); setNewDue(''); setNewNotes(''); setAdding(false); setShowDue(false); setShowNotes(false);
  };

  const handleExpandTask = (task) => {
    if (expandedTask === task.id) { setExpandedTask(null); return; }
    setExpandedTask(task.id);
    setEditingNotes(task.notes || '');
  };

  const handleSaveNotes = async (task) => {
    await onUpdateNotes(task, editingNotes);
    setExpandedTask(null);
  };

  const pending = tasks.filter(t => !t.completed);
  const done = tasks.filter(t => t.completed);

  const renderTask = (task, isDone) => (
    <div key={task.id} data-testid="gtask-item">
      <div className={cn('flex items-center gap-3 px-4 py-2.5 group', isDone && 'bg-slate-50/50')}>
        <button onClick={() => onToggle(task)}
          className={cn('w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all',
            isDone ? 'border-[#4285F4] bg-[#4285F4]' : 'border-slate-300 hover:border-[#4285F4]')}>
          {isDone && <Check size={10} className="text-white" strokeWidth={3} />}
        </button>
        <button onClick={() => handleExpandTask(task)}
          className={cn('flex-1 text-left text-sm transition-colors',
            isDone ? 'text-slate-400 line-through' : 'text-slate-800 hover:text-[#4285F4]')}>
          {task.title}
          {task.notes && expandedTask !== task.id && (
            <span className="block text-[11px] text-slate-400 font-normal mt-0.5 truncate">{task.notes}</span>
          )}
        </button>
        {task.due && expandedTask !== task.id && (
          <span className="text-[10px] font-semibold text-slate-400 shrink-0">{task.due.slice(0, 10)}</span>
        )}
        <button onClick={() => handleExpandTask(task)}
          title="Voir / éditer la note"
          className={cn('w-5 h-5 flex items-center justify-center rounded transition-all',
            expandedTask === task.id ? 'text-[#4285F4]' : 'opacity-0 group-hover:opacity-100 text-slate-300 hover:text-[#4285F4]')}>
          <FileText size={12} />
        </button>
        <button onClick={() => onDelete(task)}
          className="opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-red-400 transition-all">
          <X size={12} />
        </button>
      </div>
      {expandedTask === task.id && (
        <div className="px-4 pb-3 bg-amber-50/50 border-t border-amber-100/50">
          {task.due && (
            <p className="text-[10px] font-semibold text-slate-400 pt-2 mb-1">Échéance : {task.due.slice(0, 10)}</p>
          )}
          <textarea
            autoFocus
            value={editingNotes}
            onChange={e => setEditingNotes(e.target.value)}
            placeholder="Ajouter une note…"
            className="w-full text-xs text-slate-700 bg-transparent resize-none outline-none border-b border-slate-200 pb-1 pt-2 min-h-[50px]"
            rows={3}
          />
          <div className="flex justify-end gap-2 mt-1.5">
            <button onClick={() => setExpandedTask(null)} className="text-xs text-slate-400">Annuler</button>
            <button onClick={() => handleSaveNotes(task)} className="text-xs font-bold text-[#4285F4]">Enregistrer</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {/* List selector */}
      {lists.length > 1 && (
        <div className="flex gap-1.5 flex-wrap">
          {lists.map(l => (
            <button key={l.id} onClick={() => onSelectList(l.id)}
              className={cn('px-3 py-1.5 rounded-full text-xs font-bold border transition-all',
                selectedListId === l.id
                  ? 'bg-[#4285F4] text-white border-[#4285F4]'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#4285F4]/50')}
              data-testid={`gtask-list-${l.id}`}>
              {l.title}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="gtasks-panel">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-5 h-5 border-2 border-[#4285F4] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-50">
              {pending.map(task => renderTask(task, false))}
              {done.length > 0 && (
                <details className="group/done">
                  <summary className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-400 cursor-pointer hover:bg-slate-50 list-none">
                    <Check size={12} className="text-[#4285F4]" /> {done.length} terminée{done.length > 1 ? 's' : ''}
                  </summary>
                  {done.map(task => renderTask(task, true))}
                </details>
              )}
              {pending.length === 0 && done.length === 0 && (
                <div className="text-center py-6 text-slate-400 text-sm">Aucune tâche dans cette liste</div>
              )}
            </div>

            {/* Add task */}
            {adding ? (
              <div className="px-4 py-3 border-t border-slate-100 space-y-2">
                <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }}}
                  placeholder="Titre de la tâche…" className="w-full text-sm bg-transparent outline-none border-b border-slate-200 pb-1"
                  data-testid="gtask-new-title" />
                {showNotes && (
                  <textarea value={newNotes} onChange={e => setNewNotes(e.target.value)}
                    placeholder="Note (optionnel)…"
                    className="w-full text-xs text-slate-600 bg-slate-50 rounded-lg px-2 py-1.5 outline-none border border-slate-200 resize-none focus:border-[#4285F4]"
                    rows={2} />
                )}
                <div className="flex items-center gap-2">
                  {showDue
                    ? <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 outline-none focus:border-[#4285F4]" />
                    : <button onClick={() => setShowDue(true)} className="text-xs text-slate-400 hover:text-[#4285F4] transition-colors">+ Date</button>
                  }
                  {!showNotes && (
                    <button onClick={() => setShowNotes(true)} className="text-xs text-slate-400 hover:text-[#4285F4] transition-colors">+ Note</button>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => { setAdding(false); setNewTitle(''); setNewNotes(''); setShowDue(false); setShowNotes(false); }} className="text-xs text-slate-400">Annuler</button>
                    <button onClick={handleCreate} className="text-xs font-bold text-[#4285F4]">Ajouter</button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => setAdding(true)}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-slate-400 hover:text-[#4285F4] hover:bg-[#4285F4]/5 transition-colors"
                data-testid="add-gtask-btn">
                <Plus size={13} /> Ajouter une tâche Google
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Fenêtre « Nouvelle tâche » : titre, choix de la liste (interne ou Google), note
function NewTaskSheet({ open, onClose, lists, gLists, onCreate }) {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const targets = [
    ...lists.map(l => ({ key: `local:${l.id}`, label: `${l.icon || '📋'} ${l.name}`, color: l.color })),
    ...gLists.map(l => ({ key: `google:${l.id}`, label: `G · ${l.title}`, color: '#4285F4' })),
  ];
  const current = target || targets[0]?.key || '';
  if (!open) return null;
  const close = () => { setTitle(''); setNotes(''); onClose(); };
  const save = async () => {
    if (!title.trim() || !current) return;
    setSaving(true);
    try {
      const [kind, id] = current.split(/:(.+)/);
      await onCreate(kind, id, title.trim(), notes.trim());
      setTarget(current);  // mémorise la dernière liste utilisée
      close();
    } finally { setSaving(false); }
  };
  return (
    <ActionSheet open onClose={close} title="Nouvelle tâche" testId="new-task-sheet">
      <div className="px-2 space-y-3 pb-1">
        <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre de la tâche…"
          onKeyDown={e => e.key === 'Enter' && save()} data-testid="new-task-title"
          className="w-full h-12 px-3 rounded-xl border border-slate-200 text-base outline-none focus:border-[#0288D1]" />
        {targets.length === 0 ? (
          <p className="text-sm text-amber-600 px-1">Crée d'abord une liste (« Nouvelle liste » en bas de l'onglet Tâches).</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {targets.map(t => (
              <Chip key={t.key} active={current === t.key} onClick={() => setTarget(t.key)} color={t.color}>{t.label}</Chip>
            ))}
          </div>
        )}
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note (optionnel)…" rows={3}
          data-testid="new-task-notes"
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-base outline-none resize-none focus:border-[#0288D1]" />
        <button onClick={save} disabled={!title.trim() || !current || saving} data-testid="new-task-save"
          className="w-full h-12 rounded-xl bg-[#0288D1] text-white font-semibold disabled:opacity-40">
          {saving ? '…' : 'Ajouter la tâche'}
        </button>
      </div>
    </ActionSheet>
  );
}

export default function Habits() {
  const [activeTab, setActiveTab] = useState('tasks');
  const [showNewTask, setShowNewTask] = useState(false);
  const location = useLocation();
  const [taskQuery, setTaskQuery] = useState(() => location.state?.taskQuery || '');  // recherche dans les tâches (y compris terminées)
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState([]);  // logs for current week
  const [stats, setStats] = useState(null);
  const [taskLists, setTaskLists] = useState([]);
  const [tasksMap, setTasksMap] = useState({});  // { list_id: [task, ...] }
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [celebrating, setCelebrating] = useState(null);
  const [editHabit, setEditHabit] = useState(null);
  const [showHabitForm, setShowHabitForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingList, setAddingList] = useState(false);
  const [newListForm, setNewListForm] = useState({ name: '', color: '#0288D1', icon: '📋' });

  // Google Tasks state
  const [gtasksConnected, setGtasksConnected] = useState(false);
  const [gtaskLists, setGtaskLists] = useState([]);
  const [selectedGtaskList, setSelectedGtaskList] = useState('');
  const [gtasksMap, setGtasksMap] = useState({});  // { list_id: [task, ...] }
  const [gtasksLoading, setGtasksLoading] = useState(false);

  const today = new Date();
  const selectedDateStr = fmtDate(selectedDate);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weekStart = fmtDate(startOfWeek(today, { weekStartsOn: 1 }));
      const weekEnd   = fmtDate(endOfWeek(today, { weekStartsOn: 1 }));
      const [hs, ls, tls, st] = await Promise.all([
        api.get('/habits'),
        api.get(`/habits/logs?start=${weekStart}&end=${weekEnd}`),
        api.get('/task-lists'),
        api.get('/habits/stats'),
      ]);
      setHabits(hs || []);
      setLogs(ls || []);
      setTaskLists(tls || []);
      setStats(st || null);

      // Fetch tasks per list
      if (tls?.length) {
        const taskResults = await Promise.all(tls.map(tl => api.get(`/tasks?list_id=${tl.id}`)));
        const map = {};
        tls.forEach((tl, i) => { map[tl.id] = taskResults[i] || []; });
        setTasksMap(map);
      }

      // Google Tasks
      const gStatus = await api.get('/gtasks/status').catch(() => ({ connected: false }));
      setGtasksConnected(gStatus?.connected || false);
      if (gStatus?.connected) {
        const gLists = await api.get('/gtasks/lists').catch(() => []);
        setGtaskLists(gLists || []);
        if (gLists?.length) {
          const firstId = gLists[0].id;
          setSelectedGtaskList(prev => prev || firstId);
          const id = selectedGtaskList || firstId;
          const gTasks = await api.get(`/gtasks/tasks?tasklist_id=${id}`).catch(() => []);
          setGtasksMap(prev => ({ ...prev, [id]: gTasks || [] }));
        }
      }
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isCompleted = (habitId, dateStr) =>
    logs.some(l => l.habit_id === habitId && l.date === dateStr);

  const handleToggle = async (habit) => {
    const dateStr = selectedDateStr;
    const wasCompleted = isCompleted(habit.id, dateStr);
    if (!wasCompleted) {
      setCelebrating(habit.id);
      setTimeout(() => setCelebrating(null), 600);
    }
    // Optimistic update
    if (wasCompleted) {
      setLogs(prev => prev.filter(l => !(l.habit_id === habit.id && l.date === dateStr)));
    } else {
      setLogs(prev => [...prev, { id: 'tmp', habit_id: habit.id, date: dateStr }]);
    }
    try {
      await api.post('/habits/logs', { habit_id: habit.id, date: dateStr });
      if (!wasCompleted) toast.success(`✓ ${habit.name}`, { duration: 1500 });
      if (activeTab === 'stats') setStats(await api.get('/habits/stats'));
    } catch { fetchData(); }
  };

  const handleSaveHabit = async (form) => {
    try {
      if (editHabit?.id) {
        await api.put(`/habits/${editHabit.id}`, form);
      } else {
        await api.post('/habits', form);
      }
      toast.success('Habitude enregistrée');
      setShowHabitForm(false); setEditHabit(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDeleteHabit = async (id) => {
    if (!window.confirm('Supprimer cette habitude ?')) return;
    await api.delete(`/habits/${id}`);
    fetchData();
  };

  const handleAddTask = async (listId, title, description = '') => {
    const task = await api.post('/tasks', { list_id: listId, title, description, order: (tasksMap[listId]?.length || 0) });
    setTasksMap(prev => ({ ...prev, [listId]: [...(prev[listId] || []), task] }));
  };

  const handleUpdateTaskNotes = async (task, description) => {
    const updated = await api.put(`/tasks/${task.id}`, { description });
    setTasksMap(prev => ({ ...prev, [task.list_id]: prev[task.list_id].map(t => t.id === task.id ? updated : t) }));
    toast.success('Note enregistrée');
  };

  // Bouton « + » : tâche dans une liste interne ou Google
  const handleCreateFromSheet = async (kind, listId, title, notes) => {
    if (kind === 'local') await handleAddTask(listId, title, notes);
    else await handleGtaskCreate(title, null, notes || null, listId);
    toast.success('Tâche ajoutée');
  };

  const handleToggleTask = async (task) => {
    const updated = await api.put(`/tasks/${task.id}`, { completed: !task.completed });
    setTasksMap(prev => ({ ...prev, [task.list_id]: prev[task.list_id].map(t => t.id === task.id ? updated : t) }));
  };

  const handleDeleteTask = async (task_id) => {
    const task = Object.values(tasksMap).flat().find(t => t.id === task_id);
    if (!task) return;
    await api.delete(`/tasks/${task_id}`);
    setTasksMap(prev => ({ ...prev, [task.list_id]: prev[task.list_id].filter(t => t.id !== task_id) }));
  };

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Supprimer cette liste et ses tâches ?')) return;
    await api.delete(`/task-lists/${listId}`);
    setTaskLists(prev => prev.filter(tl => tl.id !== listId));
    const newMap = { ...tasksMap }; delete newMap[listId]; setTasksMap(newMap);
  };

  const handleAddList = async () => {
    if (!newListForm.name.trim()) return;
    const tl = await api.post('/task-lists', newListForm);
    setTaskLists(prev => [...prev, tl]);
    setTasksMap(prev => ({ ...prev, [tl.id]: [] }));
    setAddingList(false); setNewListForm({ name: '', color: '#0288D1', icon: '📋' });
  };

  // ── Google Tasks handlers ────────────────────────────────────────────────
  const fetchGtaskList = async (listId) => {
    setGtasksLoading(true);
    try {
      const tasks = await api.get(`/gtasks/tasks?tasklist_id=${listId}`);
      setGtasksMap(prev => ({ ...prev, [listId]: tasks || [] }));
    } finally { setGtasksLoading(false); }
  };

  const handleSelectGtaskList = (id) => {
    setSelectedGtaskList(id);
    if (!gtasksMap[id]) fetchGtaskList(id);
  };

  const handleGtaskToggle = async (task) => {
    const updated = await api.patch(`/gtasks/tasks/${task.id}?tasklist_id=${task.tasklist_id}`, { completed: !task.completed });
    setGtasksMap(prev => ({
      ...prev,
      [task.tasklist_id]: (prev[task.tasklist_id] || []).map(t => t.id === task.id ? updated : t)
    }));
  };

  const handleGtaskDelete = async (task) => {
    await api.delete(`/gtasks/tasks/${task.id}?tasklist_id=${task.tasklist_id}`);
    setGtasksMap(prev => ({
      ...prev,
      [task.tasklist_id]: (prev[task.tasklist_id] || []).filter(t => t.id !== task.id)
    }));
    toast.success('Tâche supprimée');
  };

  const handleGtaskCreate = async (title, dueDate, notes, listId = selectedGtaskList) => {
    if (!listId || !title.trim()) return;
    const task = await api.post('/gtasks/tasks', {
      tasklist_id: listId,
      title: title.trim(),
      due: dueDate || null,
      notes: notes || null,
    });
    setGtasksMap(prev => ({
      ...prev,
      [listId]: [...(prev[listId] || []), task]
    }));
  };

  // Recherche : charger toutes les listes Google pour chercher partout
  useEffect(() => {
    if (!taskQuery || !gtasksConnected) return;
    gtaskLists.filter(l => !gtasksMap[l.id]).forEach(l => fetchGtaskList(l.id));
  }, [taskQuery, gtasksConnected, gtaskLists]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = taskQuery.trim().toLowerCase();
  const matches = (t) => q && `${t.title || ''} ${t.description || ''} ${t.notes || ''}`.toLowerCase().includes(q);
  const searchResults = q ? [
    ...taskLists.flatMap(l => (tasksMap[l.id] || []).filter(matches).map(t => ({ task: t, kind: 'local', listName: `${l.icon || ''} ${l.name}`, color: l.color }))),
    ...gtaskLists.flatMap(l => (gtasksMap[l.id] || []).filter(matches).map(t => ({ task: t, kind: 'google', listName: `G · ${l.title}`, color: '#4285F4' }))),
  ].sort((a, b) => Number(a.task.completed) - Number(b.task.completed)) : [];

  const handleGtaskUpdateNotes = async (task, notes) => {
    const updated = await api.patch(`/gtasks/tasks/${task.id}?tasklist_id=${task.tasklist_id}`, { notes });
    setGtasksMap(prev => ({
      ...prev,
      [task.tasklist_id]: (prev[task.tasklist_id] || []).map(t => t.id === task.id ? updated : t)
    }));
    toast.success('Note enregistrée');
  };

  // Habits due on selected day, grouped by time_of_day
  const dueHabits = habits.filter(h => h.is_active && isHabitDueOn(h, selectedDate));
  const grouped = TIME_ORDER.reduce((acc, time) => {
    const hs = dueHabits.filter(h => h.time_of_day === time);
    if (hs.length) acc[time] = hs;
    return acc;
  }, {});

  const totalDue = dueHabits.length;
  const totalDone = dueHabits.filter(h => isCompleted(h.id, selectedDateStr)).length;
  const pct = totalDue > 0 ? Math.round(100 * totalDone / totalDue) : 0;

  // Week strip
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: endOfWeek(today, { weekStartsOn: 1 }) });

  return (
    <div className="max-w-5xl mx-auto pb-6">
      <MobileHeader title="Tâches"
        search={{ value: taskQuery, onChange: (v) => { setTaskQuery(v); if (v) setActiveTab('tasks'); }, placeholder: 'Chercher une tâche…', scope: 'tasks' }}
        actions={activeTab === 'today'
          ? <HeaderAction icon={Plus} label="Nouvelle habitude" primary onClick={() => { setEditHabit(null); setShowHabitForm(true); }} />
          : <HeaderAction icon={Plus} label="Nouvelle tâche" primary onClick={() => setShowNewTask(true)} testId="new-task-btn" />} />
      <NewTaskSheet open={showNewTask} onClose={() => setShowNewTask(false)}
        lists={taskLists} gLists={gtasksConnected ? gtaskLists : []} onCreate={handleCreateFromSheet} />
      {/* Header */}

      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-2xl mb-4 lg:mb-6">
        {[['tasks', Clock, 'Tâches'], ['today', CheckSquare, 'Aujourd\'hui'], ['stats', BarChart2, 'Statistiques'], ['rules', ScrollText, 'Mes règles']].map(([tab, Icon, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={cn('flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold transition-all',
              activeTab === tab ? 'bg-white text-[#0288D1] shadow-sm' : 'text-slate-500 hover:text-slate-700')}
            data-testid={`tab-${tab}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#26A69A] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && activeTab === 'today' && (
        <div className="space-y-5">
          {/* Date nav */}
          <div className="flex items-center justify-between">
            <button onClick={() => setSelectedDate(d => subDays(d, 1))} className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center hover:border-slate-300 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <div className="text-center">
              <div className="font-bold text-slate-900 text-lg capitalize">
                {isToday(selectedDate) ? 'Aujourd\'hui' : format(selectedDate, 'EEEE', { locale: fr })}
              </div>
              <div className="text-sm text-slate-400">{format(selectedDate, 'd MMMM yyyy', { locale: fr })}</div>
            </div>
            <button onClick={() => setSelectedDate(d => addDays(d, 1))}
              disabled={selectedDate >= today}
              className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center hover:border-slate-300 transition-colors disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Week strip */}
          <div className="flex gap-1">
            {weekDays.map(d => {
              const dStr = fmtDate(d);
              const dayHabits = habits.filter(h => h.is_active && isHabitDueOn(h, d));
              const dayDone = dayHabits.filter(h => isCompleted(h.id, dStr)).length;
              const dayPct = dayHabits.length > 0 ? dayDone / dayHabits.length : -1;
              const isSelected = dStr === selectedDateStr;
              const isFuture = d > today;
              return (
                <button key={dStr} onClick={() => !isFuture && setSelectedDate(d)}
                  disabled={isFuture}
                  className={cn('flex-1 flex flex-col items-center py-2 rounded-xl transition-all',
                    isSelected ? 'bg-[#0288D1] text-white' : 'hover:bg-slate-100 text-slate-600',
                    isFuture && 'opacity-30 cursor-default')}>
                  <span className="text-[10px] font-bold mb-1">{DAY_NAMES[(d.getDay() + 6) % 7]}</span>
                  <span className="text-sm font-extrabold">{format(d, 'd')}</span>
                  <div className={cn('w-2 h-2 rounded-full mt-1 transition-colors',
                    isFuture ? 'bg-transparent'
                    : dayPct < 0 ? 'bg-slate-200'
                    : dayPct === 1 ? (isSelected ? 'bg-white' : 'bg-[#26A69A]')
                    : dayPct > 0 ? (isSelected ? 'bg-white/70' : 'bg-amber-400')
                    : (isSelected ? 'bg-white/30' : 'bg-slate-200'))} />
                </button>
              );
            })}
          </div>

          {/* Progress ring */}
          {totalDue > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex items-center gap-4">
              <div className="relative w-16 h-16 shrink-0">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none" strokeWidth="3"
                    strokeDasharray={`${pct} ${100 - pct}`}
                    strokeLinecap="round"
                    stroke={pct === 100 ? '#26A69A' : pct >= 50 ? '#F59E0B' : '#0288D1'} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-extrabold text-slate-800">{pct}%</span>
                </div>
              </div>
              <div>
                <div className="font-bold text-slate-900">{totalDone} / {totalDue} habitudes</div>
                <div className="text-sm text-slate-400">
                  {pct === 100 ? '🎉 Journée parfaite !' : pct >= 50 ? 'Bonne progression !' : 'Continuez !'}
                </div>
              </div>
              <button onClick={() => { setEditHabit(null); setShowHabitForm(true); }}
                className="ml-auto w-10 h-10 bg-[#26A69A] text-white rounded-xl flex items-center justify-center hover:bg-[#219e92] transition-colors"
                data-testid="add-habit-btn">
                <Plus size={18} />
              </button>
            </div>
          )}

          {/* Habit groups */}
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">💃</div>
              <div className="font-bold text-slate-700 mb-1">Aucune habitude aujourd'hui</div>
              <div className="text-sm text-slate-400 mb-4">Commencez par créer vos premières habitudes</div>
              <button onClick={() => { setEditHabit(null); setShowHabitForm(true); }}
                className="bg-[#26A69A] text-white font-bold px-5 py-2.5 rounded-xl hover:bg-[#219e92] transition-colors">
                + Créer une habitude
              </button>
            </div>
          ) : (
            Object.entries(grouped).map(([time, hs]) => (
              <div key={time}>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{TIME_LABELS[time]}</div>
                <div className="space-y-2">
                  {hs.map(habit => {
                    const done = isCompleted(habit.id, selectedDateStr);
                    const streak = stats?.habits?.find(r => r.habit_id === habit.id)?.streak || 0;
                    const isCelebrating = celebrating === habit.id;
                    return (
                      <div key={habit.id}
                        className={cn('flex items-center gap-3 p-3 bg-white rounded-xl border shadow-sm transition-all duration-200',
                          done ? 'border-[color]/30' : 'border-slate-100',
                          isCelebrating && 'scale-[1.02]')}
                        style={{ '--color': habit.color }}>
                        <button
                          onClick={() => handleToggle(habit)}
                          className={cn(
                            'w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 border-2 shrink-0',
                            done ? 'shadow-sm' : 'border-slate-200 bg-white hover:scale-105',
                            isCelebrating && 'scale-125'
                          )}
                          style={done ? { backgroundColor: habit.color, borderColor: habit.color, boxShadow: `0 0 0 4px ${habit.color}20` } : {}}
                          data-testid={`habit-check-${habit.id}`}>
                          {done
                            ? <Check size={20} className="text-white" strokeWidth={3} />
                            : <span className="text-xl">{habit.icon}</span>
                          }
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={cn('font-semibold text-sm', done ? 'text-slate-400 line-through' : 'text-slate-800')}>
                            {habit.name}
                          </div>
                          {habit.description && !done && (
                            <div className="text-xs text-slate-400 truncate">{habit.description}</div>
                          )}
                        </div>
                        {streak > 0 && (
                          <div className="flex items-center gap-0.5 text-xs font-bold text-orange-500 shrink-0">
                            <Flame size={12} /> {streak}
                          </div>
                        )}
                        <button onClick={() => { setEditHabit(habit); setShowHabitForm(true); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-200 hover:text-slate-400 hover:bg-slate-50 transition-colors shrink-0">
                          <Edit2 size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {/* Inactive habits summary */}
          {habits.filter(h => h.is_active && !isHabitDueOn(h, selectedDate)).length > 0 && (
            <div className="text-xs text-slate-400 text-center">
              + {habits.filter(h => h.is_active && !isHabitDueOn(h, selectedDate)).length} habitudes non prévues ce jour
            </div>
          )}
        </div>
      )}

      {/* Résultats de recherche (toutes les listes, tâches terminées comprises) */}
      {!loading && activeTab === 'tasks' && q && (
        <div className="space-y-2" data-testid="task-search-results">
          <div className="text-sm text-slate-500 px-1">
            {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} pour « {taskQuery.trim()} »
          </div>
          {searchResults.map(({ task, kind, listName, color }) => (
            <div key={`${kind}-${task.id}`} className="bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 flex items-start gap-3">
              <button onClick={() => (kind === 'local' ? handleToggleTask(task) : handleGtaskToggle(task))}
                className="w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0"
                style={task.completed ? { backgroundColor: color, borderColor: color } : { borderColor: '#cbd5e1' }}>
                {task.completed && <Check size={11} className="text-white" strokeWidth={3} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className={cn('text-sm', task.completed ? 'line-through text-slate-400' : 'text-slate-800')}>{task.title}</div>
                {(task.description || task.notes) && <div className="text-xs text-slate-400 mt-0.5">{task.description || task.notes}</div>}
                <div className="text-[11px] font-semibold mt-1" style={{ color }}>{listName}{task.completed ? ' · terminée' : ''}</div>
              </div>
            </div>
          ))}
          {searchResults.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">Aucune tâche trouvée</div>}
        </div>
      )}

      {!loading && activeTab === 'tasks' && !q && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-slate-800">Mes listes</h2>
          </div>
          {taskLists.map(tl => (
            <TaskListCard key={tl.id} list={tl} tasks={tasksMap[tl.id] || []}
              onAddTask={handleAddTask} onToggleTask={handleToggleTask}
              onDeleteTask={handleDeleteTask} onDeleteList={handleDeleteList} onUpdateNotes={handleUpdateTaskNotes} />
          ))}

          {/* Add list */}
          {addingList ? (
            <div className="bg-white rounded-2xl border border-[#0288D1]/30 shadow-sm p-4 space-y-3">
              <div className="flex gap-2">
                <input value={newListForm.name} onChange={e => setNewListForm(f => ({...f, name: e.target.value}))}
                  placeholder="Nom de la liste" autoFocus className="input-base flex-1"
                  onKeyDown={e => e.key === 'Enter' && handleAddList()} data-testid="new-list-name-input" />
                <input value={newListForm.icon} onChange={e => setNewListForm(f => ({...f, icon: e.target.value}))}
                  className="input-base w-16 text-center text-xl" maxLength={2} />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setNewListForm(f => ({...f, color: c}))}
                    className={cn('w-7 h-7 rounded-full border-3 transition-all', newListForm.color === c ? 'border-slate-700 scale-110' : 'border-transparent')}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setAddingList(false)} className="flex-1 h-10 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm">Annuler</button>
                <button onClick={handleAddList} className="flex-1 h-10 rounded-xl bg-[#0288D1] text-white font-semibold text-sm" data-testid="save-list-btn">Créer</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingList(true)}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-[#0288D1] hover:text-[#0288D1] transition-colors text-sm font-semibold"
              data-testid="add-list-btn">
              <Plus size={16} /> Nouvelle liste
            </button>
          )}

          {/* ── Google Tasks section ── */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="font-bold text-slate-800 text-sm">Google Tasks</span>
              {gtasksConnected
                ? <span className="text-[10px] font-bold text-[#26A69A] bg-[#26A69A]/10 px-2 py-0.5 rounded-full ml-auto">Connecté</span>
                : <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-auto border border-amber-200">Non activé</span>
              }
            </div>

            {!gtasksConnected ? (
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 flex items-start gap-3">
                <AlertCircle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-1">Google Tasks non activé</div>
                  <div className="text-xs text-slate-500 mb-3">Reconnectez votre compte Google depuis le Profil pour activer la synchronisation des tâches.</div>
                  <a href="/profile" className="text-xs font-bold text-[#0288D1] hover:underline">→ Aller au Profil</a>
                </div>
              </div>
            ) : (
              <GoogleTasksPanel
                lists={gtaskLists}
                selectedListId={selectedGtaskList}
                tasks={gtasksMap[selectedGtaskList] || []}
                loading={gtasksLoading}
                onSelectList={handleSelectGtaskList}
                onToggle={handleGtaskToggle}
                onDelete={handleGtaskDelete}
                onCreate={handleGtaskCreate}
                onUpdateNotes={handleGtaskUpdateNotes}
              />
            )}
          </div>
        </div>
      )}

      {activeTab === 'rules' && (
        <EntryList space="regles" canImport addLabel="Nouvelle" intro="Tes règles de vie, à relire quand tu en as besoin." />
      )}

      {!loading && activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Global progress bars */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-4">
            <h2 className="font-bold text-slate-800">Vue d'ensemble</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <PctBar pct={stats.global.today.pct} color="#0288D1" label="Aujourd'hui"
                sub={`${stats.global.today.completed}/${stats.global.today.expected}`} />
              <PctBar pct={stats.global.week.expected > 0 ? Math.round(100*stats.global.week.completed/stats.global.week.expected) : 0}
                color="#26A69A" label="Cette semaine"
                sub={`${stats.global.week.completed}/${stats.global.week.expected}`} />
              <PctBar pct={stats.global.month.expected > 0 ? Math.round(100*stats.global.month.completed/stats.global.month.expected) : 0}
                color="#F57C00" label="Ce mois"
                sub={`${stats.global.month.completed}/${stats.global.month.expected}`} />
              <PctBar pct={stats.global.year.expected > 0 ? Math.round(100*stats.global.year.completed/stats.global.year.expected) : 0}
                color="#7B1FA2" label="Cette année"
                sub={`${stats.global.year.completed}/${stats.global.year.expected}`} />
            </div>
          </div>

          {/* Per-habit stats table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-50">
              <h2 className="font-bold text-slate-800">Par habitude</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-50">
                    <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Habitude</th>
                    <th className="text-center px-2 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider">🔥</th>
                    <th className="text-center px-2 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider">7j</th>
                    <th className="text-center px-2 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider">30j</th>
                    <th className="text-center px-2 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Année</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {stats.habits.map(r => (
                    <tr key={r.habit_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-base shrink-0"
                            style={{ backgroundColor: r.habit_color + '20' }}>{r.habit_icon}</span>
                          <span className="font-semibold text-slate-800 text-sm truncate max-w-[120px]">{r.habit_name}</span>
                        </div>
                      </td>
                      <td className="text-center px-2 py-3">
                        <span className={cn('text-sm font-bold', r.streak > 0 ? 'text-orange-500' : 'text-slate-300')}>
                          {r.streak > 0 ? r.streak : '—'}
                        </span>
                      </td>
                      <td className="text-center px-2 py-3">
                        <span className={cn('text-sm font-bold', r.week.pct >= 80 ? 'text-[#26A69A]' : r.week.pct >= 50 ? 'text-amber-500' : 'text-slate-400')}>
                          {r.week.pct}%
                        </span>
                      </td>
                      <td className="text-center px-2 py-3">
                        <span className={cn('text-sm font-bold', r.month.pct >= 80 ? 'text-[#26A69A]' : r.month.pct >= 50 ? 'text-amber-500' : 'text-slate-400')}>
                          {r.month.pct}%
                        </span>
                      </td>
                      <td className="text-center px-2 py-3">
                        <span className={cn('text-sm font-bold', r.year.pct >= 80 ? 'text-[#26A69A]' : r.year.pct >= 50 ? 'text-amber-500' : 'text-slate-400')}>
                          {r.year.pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Manage habits link */}
          <button onClick={() => setActiveTab('today')}
            className="w-full py-3 text-sm font-semibold text-[#0288D1] hover:bg-[#0288D1]/5 rounded-2xl transition-colors">
            ← Retour aux habitudes
          </button>
        </div>
      )}

      {/* Habit form modal */}
      {showHabitForm && (
        <HabitForm
          habit={editHabit}
          onSave={handleSaveHabit}
          onCancel={() => { setShowHabitForm(false); setEditHabit(null); }}
        />
      )}
      {editHabit && !showHabitForm && (
        <div className="fixed bottom-24 right-4 z-30">
          <button onClick={() => handleDeleteHabit(editHabit.id)}
            className="w-11 h-11 bg-red-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors">
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
